import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { chargerCatalogue, versSlug } from '../catalogue';
import { estVideo, toutesLesOeuvres, type Catalogue, type MediaPublic } from '../types';
import { Visionneuse } from '../composants/Visionneuse';
import { EnTete } from '../composants/EnTete';
import { Grille } from '../composants/Grille';
import { Lecture, SoulignementRubrique } from '../composants/Icones';

export function Galerie() {
  const { slug } = useParams<{ slug: string }>();
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [indexOuvert, setIndexOuvert] = useState<number | null>(null);

  useEffect(() => {
    const controleur = new AbortController();
    chargerCatalogue(controleur.signal)
      .then(setCatalogue)
      .catch((error: unknown) => {
        if (controleur.signal.aborted) return;
        setErreur(error instanceof Error ? error.message : 'Chargement impossible');
      });
    return () => controleur.abort();
  }, []);

  // Sans slug, on est sur l'accueil : aucune rubrique n'est active.
  const rubriqueActive = useMemo(() => {
    if (!catalogue || !slug) return null;
    return catalogue.rubriques.find((r) => versSlug(r.libelle) === slug) ?? null;
  }, [catalogue, slug]);

  /**
   * Ce qui est affiché : l'ensemble des œuvres sur l'accueil, la rubrique choisie
   * sinon. C'est le comportement du site d'origine — et sans lui, les 24 œuvres
   * sans rubrique n'apparaîtraient nulle part.
   */
  const mediasAffiches = useMemo(() => {
    if (!catalogue) return [];
    return rubriqueActive ? rubriqueActive.medias : toutesLesOeuvres(catalogue);
  }, [catalogue, rubriqueActive]);

  // Le titre suit la rubrique consultée : une page dont le titre ne change jamais
  // n'est indexable que sous une seule entrée, quelle que soit l'œuvre affichée.
  useEffect(() => {
    document.title = rubriqueActive
      ? `${rubriqueActive.libelle} — Jeronimus`
      : 'Jeronimus — art visuel, pixels et mouvement';
  }, [rubriqueActive]);

  return (
    <>
      <EnTete
        rubriques={
          <nav className="rubriques">
            {catalogue?.rubriques.map((rubrique) => {
              const active = rubrique.id === rubriqueActive?.id;
              return (
                <Link
                  key={rubrique.id}
                  to={`/r/${versSlug(rubrique.libelle)}`}
                  className={active ? 'active' : undefined}
                >
                  {rubrique.libelle}
                  {/* Le soulignement dessiné par Dorian, et non un trait droit. */}
                  <SoulignementRubrique className="soulignement" />
                </Link>
              );
            })}
          </nav>
        }
      />

      <main>
        {erreur && <p className="message">La galerie n’a pas pu être chargée. {erreur}</p>}

        {!catalogue && !erreur && (
          <div className="grille" aria-hidden="true">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="squelette" />
            ))}
          </div>
        )}

        {catalogue && (
          <Grille
            medias={mediasAffiches}
            rendre={(media, index) => (
              <Vignette key={media.cle} media={media} onOuvrir={() => setIndexOuvert(index)} />
            )}
          />
        )}

        {catalogue && mediasAffiches.length === 0 && (
          <p className="message">Cette rubrique est vide pour le moment.</p>
        )}
      </main>

      {indexOuvert !== null && (
        <Visionneuse
          medias={mediasAffiches}
          indexInitial={indexOuvert}
          onFermer={() => setIndexOuvert(null)}
        />
      )}
    </>
  );
}

function Vignette({ media, onOuvrir }: { media: MediaPublic; onOuvrir: () => void }) {
  return (
    <button className="vignette" onClick={onOuvrir} aria-label={`Ouvrir ${media.libelle}`}>
      {media.urlVignette ? (
        <img
          src={media.urlVignette}
          alt={media.libelle}
          // Le navigateur ne télécharge que ce qui approche de l'écran : une grille
          // de 30 œuvres ne coûte plus 30 téléchargements à l'ouverture de la page.
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="vignette-absente">{media.libelle}</span>
      )}
      {estVideo(media) && (
        <span className="badge-video" aria-hidden="true">
          <Lecture />
        </span>
      )}
    </button>
  );
}

